import React from 'react';
import { GenericToolRenderer } from './GenericToolRenderer';
import { GenericPdfGeneratorTool } from './GenericPdfGeneratorTool';
import { BingoCallerBoard } from './BingoCallerBoard';
import { BingoCardGenerator } from './BingoCardGenerator';
import { AlumniAnnualFundModeler } from './AlumniAnnualFundModeler';
import { AlumniDensityMapper } from './AlumniDensityMapper';
import { ClassReunionEstimator } from './ClassReunionEstimator';
import { EndowmentRunwayCalculator } from './EndowmentRunwayCalculator';
import { UnionStrikeFundCalculator } from './UnionStrikeFundCalculator';
import { UnionWageProgression } from './UnionWageProgression';
import { GuildRatioChecker } from './GuildRatioChecker';
import { GuildRateCardBuilder } from './GuildRateCardBuilder';
import { MultiChapterBallotValidator } from './MultiChapterBallotValidator';
import { GreekRushROI } from './GreekRushROI';
import { CompositePhotoCalculator } from './CompositePhotoCalculator';
import { ChapterHouseRentModeler } from './ChapterHouseRentModeler';
import { PanhellenicQuotaCalculator } from './PanhellenicQuotaCalculator';
import { CanvassingRouteOptimizer } from './CanvassingRouteOptimizer';
import { BallotInitiativeForecaster } from './BallotInitiativeForecaster';
import { PacContributionChecker } from './PacContributionChecker';
import { HoaReserveFundCalculator } from './HoaReserveFundCalculator';
import { ChurchSmallGroupRebalancer } from './ChurchSmallGroupRebalancer';
import { BoardSkillGapAnalyzer } from './BoardSkillGapAnalyzer';
import { Nonprofit990RatioChecker } from './Nonprofit990RatioChecker';
import { ConferencePnlSimulator } from './ConferencePnlSimulator';
import { CertificationPricingCalculator } from './CertificationPricingCalculator';
import { VirtualPrizeWheel } from './VirtualPrizeWheel';
import { InteractiveScoreboard } from './InteractiveScoreboard';
import { DigitalRaffleDrum } from './DigitalRaffleDrum';
import { IceBreakerSlotMachine } from './IceBreakerSlotMachine';
import { TournamentBracketGenerator } from './TournamentBracketGenerator';
import { RoundRobinScheduleGenerator } from './RoundRobinScheduleGenerator';
import { SnakeDraftTeamBalancer } from './SnakeDraftTeamBalancer';
import { HoaSpecialAssessmentCalculator } from './HoaSpecialAssessmentCalculator';
import { WorshipServiceRunSheet } from './WorshipServiceRunSheet';
import { PhilanthropyProfitMarginPredictor } from './PhilanthropyProfitMarginPredictor';
import { HQPitchGenerator } from './HQPitchGenerator';
import { RaffleTicketGenerator } from './RaffleTicketGenerator';
import { BoardMeetingAgendaBuilder } from './BoardMeetingAgendaBuilder';
import { SignUpSheetGenerator } from './SignUpSheetGenerator';
import { VolunteerValueCalculator } from './VolunteerValueCalculator';
import { SwimHeatSheetGenerator } from './SwimHeatSheetGenerator';
import { AlumniMentorshipCapacity } from './AlumniMentorshipCapacity';
import { NeighborhoodAssessmentCalculator } from './NeighborhoodAssessmentCalculator';
import { BoosterGrantScanner } from './BoosterGrantScanner';
import { PtaBudgetVsActuals } from './PtaBudgetVsActuals';
import { LeagueStandingsTiebreaker } from './LeagueStandingsTiebreaker';
import { TravelTeamBudgetPlanner } from './TravelTeamBudgetPlanner';
import { ChurchCapitalCampaign } from './ChurchCapitalCampaign';

// Import our newly extracted inline components
import { DuesSplitCalculator } from './DuesSplitCalculator';
import { QuorumAndRollChecker } from './QuorumAndRollChecker';
import { BylawsTemplatesBuilder } from './BylawsTemplatesBuilder';
import { MemberRetentionEstimator } from './MemberRetentionEstimator';
import { SubmarineGame } from './SubmarineGame';
import { OrgMarketWars } from './OrgMarketWars';
import { NodeManGame } from './NodeManGame';
import { OrgleGame } from './OrgleGame';
import { OrgRunGame } from './OrgRunGame';
import { MicroMachinesGame } from './MicroMachinesGame';
import { MembershipCertificateMaker } from './MembershipCertificateMaker';
import { VolunteerHourVerification } from './VolunteerHourVerification';
import { ReverseRaffleBoard } from './ReverseRaffleBoard';

import { ErrorBoundary } from '../ErrorBoundary';
import { AlertCircle, RotateCcw } from 'lucide-react';
import { Button } from '../ui/Button';

const PDF_GENERATOR_SLUGS = [
  'silent-auction-bid-sheet',
  'reunion-name-tags',
  'ceu-certificate-maker',
  'donation-receipt-template',
  'event-itinerary-builder',
  'volunteer-sign-in-sheet',
  'hoa-violation-door-hanger',
  'banquet-place-cards',
  'storyboard-grid-printable',
  'political-door-hanger',
  'storefront-notice-sign',
  'emergency-roster-card',
  'book-club-pamphlet',
  'wifi-qr-poster',
  'neighborhood-newsletter-template',
  'roberts-rules-cheat-sheet'
];

interface ToolRendererProps {
  slug: string;
}

function ToolErrorFallback({ slug }: { slug: string }) {
  return (
    <div className="p-8 my-6 rounded-2xl border-2 border-red-200 bg-red-50/50 dark:bg-red-950/20 dark:border-red-900/50 text-center max-w-xl mx-auto">
      <div className="w-12 h-12 rounded-xl bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400 flex items-center justify-center mx-auto mb-4">
        <AlertCircle size={24} />
      </div>
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Interactive Tool Encountered an Issue</h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
        The tool <code className="px-1.5 py-0.5 rounded bg-red-100/80 dark:bg-red-900/50 font-mono text-xs text-red-800 dark:text-red-300">{slug}</code> failed to render its canvas or interactive state. The incident report has been logged.
      </p>
      <Button variant="primary" onClick={() => window.location.reload()} className="mx-auto">
        <RotateCcw size={16} className="mr-2" />
        Reload Tool
      </Button>
    </div>
  );
}

function renderTool(slug: string) {
  switch (slug) {
    case 'bingo-caller-board': return <BingoCallerBoard />;
    case 'bingo-card-generator': return <BingoCardGenerator />;
    case 'alumni-annual-fund-modeler': return <AlumniAnnualFundModeler />;
    case 'alumni-density-mapper': return <AlumniDensityMapper />;
    case 'class-reunion-estimator': return <ClassReunionEstimator />;
    case 'endowment-runway-calculator': return <EndowmentRunwayCalculator />;
    case 'union-strike-fund-calculator': return <UnionStrikeFundCalculator />;
    case 'union-wage-progression': return <UnionWageProgression />;
    case 'guild-ratio-checker': return <GuildRatioChecker />;
    case 'guild-rate-card-builder': return <GuildRateCardBuilder />;
    case 'multi-chapter-ballot-validator': return <MultiChapterBallotValidator />;
    case 'greek-rush-roi': return <GreekRushROI />;
    case 'composite-photo-calculator': return <CompositePhotoCalculator />;
    case 'chapter-house-rent-modeler': return <ChapterHouseRentModeler />;
    case 'panhellenic-quota-calculator': return <PanhellenicQuotaCalculator />;
    case 'canvassing-route-optimizer': return <CanvassingRouteOptimizer />;
    case 'ballot-initiative-forecaster': return <BallotInitiativeForecaster />;
    case 'pac-contribution-checker': return <PacContributionChecker />;
    case 'hoa-reserve-fund-calculator': return <HoaReserveFundCalculator />;
    case 'church-small-group-rebalancer': return <ChurchSmallGroupRebalancer />;
    case 'board-skill-gap-analyzer': return <BoardSkillGapAnalyzer />;
    case 'nonprofit-990-ratio-checker': return <Nonprofit990RatioChecker />;
    case 'conference-pnl-simulator': return <ConferencePnlSimulator />;
    case 'certification-pricing-calculator': return <CertificationPricingCalculator />;
    case 'virtual-prize-wheel': return <VirtualPrizeWheel />;
    case 'interactive-scoreboard': return <InteractiveScoreboard />;
    case 'digital-raffle-drum': return <DigitalRaffleDrum />;
    case 'ice-breaker-slot-machine': return <IceBreakerSlotMachine />;
    case 'tournament-bracket-generator': return <TournamentBracketGenerator />;
    case 'round-robin-schedule-generator': return <RoundRobinScheduleGenerator />;
    case 'swim-heat-sheet-generator': return <SwimHeatSheetGenerator />;
    case 'alumni-mentorship-capacity': return <AlumniMentorshipCapacity />;
    case 'neighborhood-assessment-calculator': return <NeighborhoodAssessmentCalculator />;
    case 'booster-grant-scanner': return <BoosterGrantScanner />;
    case 'pta-budget-vs-actuals': return <PtaBudgetVsActuals />;
    case 'league-standings-tiebreaker': return <LeagueStandingsTiebreaker />;
    case 'travel-team-budget-planner': return <TravelTeamBudgetPlanner />;
    case 'church-capital-campaign': return <ChurchCapitalCampaign />;
    case 'snake-draft-team-balancer': return <SnakeDraftTeamBalancer />;
    case 'hoa-special-assessment-calculator': return <HoaSpecialAssessmentCalculator />;
    case 'worship-service-run-sheet': return <WorshipServiceRunSheet />;
    case 'philanthropy-profit-margin-predictor': return <PhilanthropyProfitMarginPredictor />;
    case 'hq-pitch-generator': return <HQPitchGenerator />;
    case 'raffle-ticket-generator': return <RaffleTicketGenerator />;
    case 'meeting-agenda-template': return <BoardMeetingAgendaBuilder />;
    case 'sign-up-sheet-generator': return <SignUpSheetGenerator />;
    case 'volunteer-value-calculator': return <VolunteerValueCalculator />;
    case 'membership-certificate': return <MembershipCertificateMaker />;
    case 'volunteer-hour-verification': return <VolunteerHourVerification />;
    case 'reverse-raffle-board': return <ReverseRaffleBoard />;
    
    // Newly extracted tools
    case 'dues-split-calculator': return <DuesSplitCalculator />;
    case 'quorum-and-roll-checker': return <QuorumAndRollChecker />;
    case 'bylaws-templates-builder': return <BylawsTemplatesBuilder />;
    case 'member-retention-estimator': return <MemberRetentionEstimator />;
    case 'deep-sea-explorer': return <SubmarineGame />;
    case 'org-market-wars': return <OrgMarketWars />;
    case 'node-man': return <NodeManGame />;
    case 'orgle': return <OrgleGame />;
    case 'org-run': return <OrgRunGame />;
    case 'micro-machines': return <MicroMachinesGame />;

    default:
      if (slug && PDF_GENERATOR_SLUGS.includes(slug)) {
        return <GenericPdfGeneratorTool slug={slug} />;
      }
      return <GenericToolRenderer slug={slug || ''} />;
  }
}

export function ToolRenderer({ slug }: ToolRendererProps) {
  return (
    <ErrorBoundary fallback={<ToolErrorFallback slug={slug} />}>
      {renderTool(slug)}
    </ErrorBoundary>
  );
}
